import { MerchantLinks } from '@components/navigation/merchant/links';
import { NavigationProvider } from '@ctx/navigation';
import { useAppContext } from '@hooks/useAppContext';
import { MainLayout } from '@layouts/main';
import { useMerchantLogout } from '@services/merchant/session/delete';
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { getSlots, getUserSlot } from '@utils/apiHelpers';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

function MerchantLayout() {
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const router = useRouter();
  const { clearData, sendBcPost, user } = useAppContext();
  const { mutate: logout, isPending } = useMerchantLogout({
    successHandler: async () => {
      clearData();
      await router.invalidate();
      await navigate({ to: '/merchant/login' });
      if (sendBcPost) sendBcPost({ type: 'session', sender: getUserSlot(user?.id) });
      toast.success(t('toast.session.end'));
    },
  });

  return (
    <NavigationProvider logout={logout} links={<MerchantLinks />} logoutPending={isPending}>
      <MainLayout />
    </NavigationProvider>
  );
}

export const Route = createFileRoute('/u/$localId/_merchant')({
  beforeLoad: ({ context, location, params }) => {
    // check if logged in
    if (!context.isAuthenticated()) {
      throw redirect({ to: '/merchant/login', search: { redirect: location.href } });
    }
    // check if same user type as logged in
    if (
      !context.isValidUserType('merchant') &&
      location.pathname !== `/u/${params.localId}/merchant/forbidden`
    ) {
      const idx = getSlots().findIndex(
        (el) => el && (JSON.parse(el as string) as Record<string, unknown>).userType === 'merchant',
      );
      // if session for requested user type exist go to first found or redirect to login
      if (idx >= 0 && idx.toString() !== params.localId) {
        // eslint-disable-next-line immutable/no-mutation
        window.location.href = window.location.href.replace(`/u/${params.localId}`, `/u/${idx}`);
        return;
      }
      if (idx < 0) {
        throw redirect({ to: '/merchant/login', search: { redirect: location.href } });
      }
    }

    // check if 2fa is required and enabled or password require update
    if (
      context.user &&
      location.pathname !== `/u/${params.localId}/merchant/settings` &&
      context.partialAccess
    ) {
      throw redirect({ to: '/u/$localId/merchant/settings', params });
    }
  },
  component: MerchantLayout,
});
